import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { API } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function MemberActivate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyState, setVerifyState] = useState("loading"); // loading | ok | error
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setVerifyState("error"); setError("Activation link is missing a token."); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/auth/activate/verify?token=${encodeURIComponent(token)}`);
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.detail || "Invalid activation link");
        setMember(j);
        setVerifyState("ok");
      } catch (e) {
        setVerifyState("error");
        setError(e.message || "This activation link is invalid or has expired.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be 6+ characters."); return; }
    if (pw !== pw2) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pw }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Activation failed");
      // Auto-login: store the member token and go to dashboard
      localStorage.setItem("gt_member_token", j.token);
      toast.success(`Welcome aboard, ${j.member.name.split(' ')[0]}.`);
      navigate("/member/dashboard");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (verifyState === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6" data-testid="activate-loading">
        <div className="text-zinc-500 text-sm tracking-[0.3em] uppercase">Loading...</div>
      </div>
    );
  }

  if (verifyState === "error") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6" data-testid="activate-error">
        <div className="max-w-md w-full border border-zinc-800 bg-[#141414] p-10 text-center">
          <h1 className="font-display text-3xl mb-3">Link Expired or Invalid</h1>
          <p className="text-zinc-400 text-sm mb-6">{error}</p>
          <p className="text-zinc-500 text-xs leading-relaxed mb-6">
            If you need a new activation link, reply to your invitation email and ask Davon to resend it.
          </p>
          <Link to="/member/login" className="text-[#E5B43A] text-xs font-bold uppercase tracking-widest hover:underline">Go to Member Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-6" data-testid="activate-page">
      <form onSubmit={submit} className="w-full max-w-md border border-zinc-800 bg-[#141414] p-8 md:p-10" data-testid="activate-form">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 bg-[#E5B43A] grid place-items-center">
            <ShieldCheck size={18} className="text-black" />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A]">Activate Account</div>
            <div className="font-display text-xl tracking-wider">Set Your Password</div>
          </div>
        </div>
        <p className="text-zinc-400 text-sm mb-8 mt-3" data-testid="activate-welcome">
          Hey <span className="text-white font-semibold">{member.name.split(' ')[0]}</span> — Davon set up your Gain Time Fitness account.
          Pick a password to get in.
        </p>

        <div className="border border-zinc-800 bg-[#0F0F0F] px-3 py-2 mb-6 text-xs">
          <span className="text-zinc-500 tracking-widest uppercase">Email · </span>
          <span className="text-zinc-200 break-all">{member.email}</span>
        </div>

        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">New Password</label>
        <div className="relative mb-4">
          <input
            type={show ? "text" : "password"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            required minLength={6}
            data-testid="activate-password-input"
            className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 pr-12 text-white focus:outline-none focus:border-[#E5B43A]"
          />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-[#E5B43A]">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Confirm</label>
        <input
          type={show ? "text" : "password"}
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
          required minLength={6}
          data-testid="activate-confirm-input"
          className="w-full bg-[#0F0F0F] border border-zinc-800 px-4 py-3 mb-6 text-white focus:outline-none focus:border-[#E5B43A]"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="activate-submit"
          className="w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
        >
          {loading ? "Activating..." : <>Activate &amp; Sign In <ArrowRight size={16} /></>}
        </button>
      </form>
    </div>
  );
}
